import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Paper,
  Typography,
  IconButton,
  Box,
  FormControlLabel,
  Checkbox,
  Grid,
  CircularProgress,
  Divider,
  Chip,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
} from "@mui/icons-material";

export default function CC417({ submitForm, declarationInfo }) {
  const [goodsItemTaxes, setGoodsItemTaxes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [messageSender, setMessageSender] = useState("");
  const [messageRecipient, setMessageRecipient] = useState("");

  useEffect(() => {
    if (declarationInfo) {
      if (declarationInfo.declaration_info) {
        setMessageSender(declarationInfo.declaration_info.message_sender || "");
        setMessageRecipient(
          declarationInfo.declaration_info.message_recipient || ""
        );
      }

      // Initialize one goods item with default values
      setGoodsItemTaxes([
        {
          goods_item_number: "1",
          tax_types: [
            {
              tax_type_code: "",
              tax_payment_method: "",
            },
          ],
        },
      ]);
    }

    setLoading(false);
  }, [declarationInfo]);

  const handleAddGoodsItem = () => {
    setGoodsItemTaxes([
      ...goodsItemTaxes,
      {
        goods_item_number: (goodsItemTaxes.length + 1).toString(),
        tax_types: [
          {
            tax_type_code: "",
            tax_payment_method: "",
          },
        ],
      },
    ]);
  };

  const handleRemoveGoodsItem = (goodsItemIndex) => {
    const updated = goodsItemTaxes.filter((_, i) => i !== goodsItemIndex);
    // Renumber goods items
    const renumbered = updated.map((item, idx) => ({
      ...item,
      goods_item_number: (idx + 1).toString(),
    }));
    setGoodsItemTaxes(renumbered);
  };

  const handleAddTaxType = (goodsItemIndex) => {
    const updated = [...goodsItemTaxes];
    updated[goodsItemIndex].tax_types.push({
      tax_type_code: "",
      tax_payment_method: "",
    });
    setGoodsItemTaxes(updated);
  };

  const handleRemoveTaxType = (goodsItemIndex, taxTypeIndex) => {
    const updated = [...goodsItemTaxes];
    updated[goodsItemIndex].tax_types = updated[
      goodsItemIndex
    ].tax_types.filter((_, i) => i !== taxTypeIndex);
    setGoodsItemTaxes(updated);
  };

  const handleTaxTypeChange = (goodsItemIndex, taxTypeIndex, field, value) => {
    const updated = [...goodsItemTaxes];
    updated[goodsItemIndex].tax_types[taxTypeIndex][field] = value;
    setGoodsItemTaxes(updated);
  };

  const handleSubmit = () => {
    const requestData = {
      goods_item_taxes: goodsItemTaxes.map((item) => ({
        goods_item_number: item.goods_item_number,
        tax_types: item.tax_types.map((taxType) => ({
          tax_type_code: taxType.tax_type_code,
          tax_payment_method: taxType.tax_payment_method,
        })),
      })),
    };

    submitForm({ messageType: "CC417", data: requestData });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div style={{ margin: "20px 0" }}>
      <Paper
        sx={{ boxShadow: "none" }}
        style={{
          padding: "20px",
          marginBottom: "20px",
          backgroundColor: "#f5f5f5",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Declaration Information
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Message Sender"
              value={messageSender}
              onChange={(e) => setMessageSender(e.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Message Recipient"
              value={messageRecipient}
              onChange={(e) => setMessageRecipient(e.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              {declarationInfo?.mrn ? "MRN" : "LRN"}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1" style={{ fontWeight: 500 }}>
                {declarationInfo?.mrn || declarationInfo?.lrn || "N/A"}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Message Type
            </Typography>
            <Chip label="CC417 - H1" color="primary" />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Preparation Date & Time
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Will be automatically set to current moment when submitting
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {goodsItemTaxes.map((goodsItem, goodsItemIndex) => (
        <Paper
          sx={{ boxShadow: "none" }}
          key={goodsItemIndex}
          style={{ padding: "20px", marginBottom: "20px" }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">
              Goods Item {goodsItem.goods_item_number}
            </Typography>
            {goodsItemTaxes.length > 1 && (
              <IconButton
                color="error"
                size="small"
                onClick={() => handleRemoveGoodsItem(goodsItemIndex)}
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>

          <Typography
            variant="subtitle1"
            style={{ marginTop: "20px", marginBottom: "10px" }}
          >
            Tax Types
          </Typography>

          {goodsItem.tax_types.map((taxType, taxTypeIndex) => (
            <Paper
              sx={{ boxShadow: "none" }}
              key={taxTypeIndex}
              style={{
                padding: "15px",
                marginBottom: "10px",
                backgroundColor: "#f5f5f5",
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <TextField
                    label="Tax Type Code"
                    value={taxType.tax_type_code}
                    onChange={(e) =>
                      handleTaxTypeChange(
                        goodsItemIndex,
                        taxTypeIndex,
                        "tax_type_code",
                        e.target.value
                      )
                    }
                    fullWidth
                    required
                    placeholder="e.g., A00, B00"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Tax Payment Method"
                    value={taxType.tax_payment_method}
                    onChange={(e) =>
                      handleTaxTypeChange(
                        goodsItemIndex,
                        taxTypeIndex,
                        "tax_payment_method",
                        e.target.value
                      )
                    }
                    fullWidth
                    required
                    placeholder="e.g., A"
                  />
                </Grid>

                <Grid item xs={12} sm={1}>
                  {goodsItem.tax_types.length > 1 && (
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() =>
                        handleRemoveTaxType(goodsItemIndex, taxTypeIndex)
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Grid>
              </Grid>
            </Paper>
          ))}

          <Button
            startIcon={<AddIcon />}
            onClick={() => handleAddTaxType(goodsItemIndex)}
            style={{ marginTop: "10px" }}
          >
            Add Tax Type
          </Button>
        </Paper>
      ))}

      <Box display="flex" gap={2} justifyContent="center" mt={3} mb={2}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddGoodsItem}
        >
          Add Goods Item
        </Button>
      </Box>

      <Box display="flex" gap={2} justifyContent="center" mt={3}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SendIcon />}
          onClick={handleSubmit}
        >
          Trimite CC417
        </Button>
      </Box>
    </div>
  );
}
